import React, {
    useCallback,
    useEffect,
    useState,
    useContext
} from 'react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import _ from 'lodash';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { MLEndorsementFlowUtil, MLCapitalizeStringFilter, MLEndorsementUtil } from 'ml-portals-util-js';
import { MLNotificationBlockUtil } from 'ml-portals-util-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import appConfig from 'app-config';
import { QuestionSetsParser } from 'ml-portals-questionsets-js';
import { MLPolicyChangeDataHolderService, MLNavigationStepsTrackingService } from 'ml-services-common-react';
import { useModal } from '@jutro/components';
import { FormModalPopover } from 'ml-components-platform-react';
import { BusinessDetailsPopupComponent, UnderlyingPolicyComponent } from 'ml-capability-quoteandbind-common-react';
import addUnderlyingPolicyPopupMetadata from '../../../ml-capability-quoteandbind-common-react/pages/UnderlyingPolicies/AddEditUnderlyingPolicyPopup.metadata.json5';
import metadata from './BusinessActivityPagePolChange.metadata.json5';

import messages from './BusinessActivityPagePolChange.messages';
import styles from './BusinessActivityPagePolChange.module.scss';
import { ScrollToError } from '@jutro/legacy/wizard-next';

const alertNames = {
    addedBizActivityName: 'addedBizActivity'
};

const BusinessActivityPagePolChange = (props) => {
    const modalApi = useModal();
    const {
        wizardData,
        updateWizardData,
        goNext,
        currentStepIndex,
        steps,
        currentStep,
        changeNextSteps
    } = props;
    const { submissionVM, alertMessages } = wizardData;
    const { EndorsementService } = useDependencies('EndorsementService');
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const { authHeader } = useAuthentication();

    const {
        showProgressDialog
    } = MLModalService();

    const MLAlertService = MLNotificationBlockUtil();

    const isGPA = appConfig.portalCode === 'GPA';
    const pupModel = _.get(submissionVM, 'lobData.personalUmbrella', []);
    const underlyingPoliciesVM = _.get(submissionVM,  'lobData.personalUmbrella.underlyingPolicies');
    const isModernProduct = _.get(submissionVM, 'isModernProduct_Ext.value', false);
    const productCode = _.get(submissionVM, 'baseData.productCode.value', '');

    const [nonEditingBusActivities, setNonEditingBusActivities] = useState([]);
    const [busActivity, setBusActivity] = useState(viewModelService.create({}, 'pc', 'metlife.edge.capabilities.common.plugin.dto.PUPBusinessActivityDTOML'));
    const [pageAction, setPageAction] = useState('');
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);
    const [questionSetsMetadata, setQuestionSetsMetadata] = useState(null);

    const { onValidate } = useValidation('BusinessActivityPagePolChange');

    const updateWizardSubmissionData = useCallback((vm) => {
        _.set(wizardData, 'submissionVM', vm);
        updateWizardData(wizardData);
    }, [updateWizardData, wizardData]);


    const setFixIdsOfNonEditingBusActivities = () => {
        const noEditBusActivityPublicIDs = [];
        const businessActivitiesVal = _.get(submissionVM, 'lobData.personalUmbrella.coverables.businessActivities.value');
        businessActivitiesVal.forEach((ba) => {
            if (ba.publicID && !ba.isSelected && ba.isEditing !== true) {
                noEditBusActivityPublicIDs.push(ba.publicID);
            }
        });
        setNonEditingBusActivities(noEditBusActivityPublicIDs);
        return noEditBusActivityPublicIDs;
    };

    const setCurrentBusActivityVM = (lobVM, noEditBusActivityPublicIDs) => {
        let index = 0;
        const currentBusActivitiesVM = _.get(lobVM, 'personalUmbrella.coverables.businessActivities');
        if (noEditBusActivityPublicIDs.length > 0) {
            index = currentBusActivitiesVM.value.findIndex((ba) => noEditBusActivityPublicIDs.indexOf(ba.publicID) === -1);
        }

        let busActivityVM = currentBusActivitiesVM.getElement(index);

        if (_.isUndefined(busActivityVM)) { // To avoid changing existing logic
            const { additionalData } = currentStep;

            if (additionalData) {
                // if Additional Data is availabe
                busActivityVM = currentBusActivitiesVM.children.find((ba) => ba.value.fixedID === additionalData.fixedID);
            }

            return busActivityVM
        }

        busActivityVM.value.isSelected = true;

        return busActivityVM;
    };

    const initBusinessActivityPage = () => {
        const noEditBusActivityPublicIDs = setFixIdsOfNonEditingBusActivities();
        const currentBusActivityVM = setCurrentBusActivityVM(submissionVM.lobData, noEditBusActivityPublicIDs);
        setBusActivity(currentBusActivityVM);
        if (MLPolicyChangeDataHolderService.getPageAction() === 'add') {
            setPageAction('add');
            MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.addBusinessActivityDynamic), false, currentStep.mlStepName, authHeader);
        } else if (MLPolicyChangeDataHolderService.getPageAction() === 'edit') {
            setPageAction('edit');            
            MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.editBusinessActivityDynamic), false, currentStep.mlStepName, authHeader);
        } 
    };

    const getQuestionSetMetadata = useCallback(() => {
        import(
            /* webpackChunkName: "question-sets-metadata" */
            // eslint-disable-next-line import/no-unresolved
            'question-sets-metadata'
        ).then((data) => {
            const { default: result } = data;
            setQuestionSetsMetadata(result);
        });
    }, []);

    useEffect(() => {
        if (MLEndorsementFlowUtil.startingPointPageLoadIsSkipping(submissionVM, goNext, steps, currentStepIndex, changeNextSteps)) {
            return;
        };
        initBusinessActivityPage();
        getQuestionSetMetadata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getNextStepText = () => {
        return (
            <FormattedMessage
                {...messages.nextBaseDescription}
                values={{
                    bold: (
                        <strong>{translator(messages.nextDescriptionBold)}</strong>
                    )
                }}
            />
        );
    };

    const addNewBusinessActivityAlert = (item) => {
        const param = item.value.name;
        const alertName = alertNames.addedBizActivityName;       
        const alertMsg = (
            <FormattedMessage
                {...messages.businessActivityXHasBeenAdded}
                values={{
                    strongName: (
                        <strong>{MLCapitalizeStringFilter.capitalize(param)}</strong>
                    )
                }}
            />
        );
        // To remove the alert message on Bussiness activity Page and will be shown on additional details
        // MLAlertService.addSuccessAlert(alertMsg, alertName)
        const alert = {
            alertName: alertName,
            formattedMessageName: 'businessActivityXHasBeenAdded',
            messagePram: param
        };

        const alertExists = alertMessages.some((msg) => {
            if (_.isEqual(msg, alert)) {
                return true;
            }

            return false;
        });

        if (!alertExists) {
            alertMessages.push(alert);
        }
    };

    const getPopupProps = (underlyingVM, isEdit = false) => {
        const componentProps = {
            title: messages.addAPolicy,
            viewModel: underlyingVM,
            uiProps: addUnderlyingPolicyPopupMetadata.pageContent,
            actionBtnLabel: messages.addPolicy,
            cancelBtnLabel: messages.cancel,
            getOverrideProps: () => {
                return {
                    underlyingPolicyComponent: {
                        underlyingPolicyVM: underlyingVM,
                        quoteID: submissionVM.value.jobID,
                        onValidate,
                        isPolicyChange: true,
                        showValidationErrors,
                        isEdit,
                        isComponent: true,
                        authHeader,
                        MLAlertService,
                        isModernProduct
                    }
                };
            },
            customComponentResolvers: {
                underlyingPolicyComponent: UnderlyingPolicyComponent
            },
        };

        return componentProps;
    };

    const addUnderlyingPolicy = (ulPolicyVM) => {
        if (!(ulPolicyVM.aspects.valid && ulPolicyVM.aspects.subtreeValid)) {
            setShowValidationErrors(true);
            setErrorTimestamp(errorTimestamp+1);
            return;
        }
        busActivity.ulPolicyFixID.value = ulPolicyVM.ulPolicyFixID.value;
        updateWizardSubmissionData(submissionVM);
        return true;
    };

    const cancelAddUnderlyingPolicy = (policy) => {
        pupModel.value.removeUnderlyingPolicy(policy);
        busActivity.ulPolicyFixID.value = undefined;
        updateWizardSubmissionData(submissionVM);
    };

    const createNewULPolicy = () => {
        const newUnderlyingPolicy = pupModel.value.createUnderlyingPolicy();
        const ulPolicy = underlyingPoliciesVM.findFirstElement(newUnderlyingPolicy);

        let componentProps = getPopupProps(ulPolicy);
        componentProps = {
            ...componentProps,
            onSaveAction: addUnderlyingPolicy
        };
        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).catch(() => cancelAddUnderlyingPolicy(ulPolicy.value));
    };

    const getUnderlyingPolicies = () => {
        let addedPolicy;
        if (underlyingPoliciesVM.value.findIndex((ulPolicy) => ulPolicy.ulPolicyFixID === 'new') === -1) {
            addedPolicy = pupModel.value.createUnderlyingPolicy({
                ulPolicyFixID: 'new',
                policyNumber: translator(messages.underlyingPolicyNotListed)
            });
        }

        const mappedValues = underlyingPoliciesVM.value.map((policy) => {
            let underlyingPolicyDisplayName = policy.getDisplayName(viewModelService.productMetadata, translator);
            if(isModernProduct && policy.policyType === 'HO') {
                underlyingPolicyDisplayName = underlyingPolicyDisplayName.replace('Homeowners', 'Property');
            }
            return {
                code: policy.ulPolicyFixID,
                name: translator({
                    id: policy.policyNumber,
                    defaultMessage: underlyingPolicyDisplayName
                })
            };
        });

        if (addedPolicy !== undefined) {
            pupModel.value.removeUnderlyingPolicy(addedPolicy);
        }
        return mappedValues;
    };

    const syncExposure = () => {
        return EndorsementService.saveEndorsement([submissionVM.value], authHeader).then((updatedEndorsementData) => {
            const policyChange = new PolicyChange(Object.assign({}, updatedEndorsementData));
            const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');
            _.set(wizardData, 'submissionVM', updatedEndorsementVM);

            return updatedEndorsementVM;
        }).catch(() => {
            return submissionVM;
        });
    };

    const syncBusActivityExposure = () => {
        return syncExposure().then((updatedEndorsementVM) => setCurrentBusActivityVM(updatedEndorsementVM.lobData, nonEditingBusActivities));
    };

    const checkForQuestionSetErrors = useCallback((questionSet) => {
        const { viewModel: qsViewModel } = new QuestionSetsParser(questionSet, questionSetsMetadata, translator);
        return Object.keys(qsViewModel.answers).some((ansKey) => !_.isUndefined(qsViewModel[ansKey]) && !qsViewModel[ansKey].aspects.valid);
    }, [questionSetsMetadata, translator]);

    const scrollToFirstError = () => {
        // as soon as the first validation error appears, scroll to that element so the user can see it
        const firstErrorInterval = setInterval(() => {
            const errors =  document.getElementsByClassName('jut__OldFieldMessage__error');
            if (!_.isUndefined(errors) && errors.length > 0) {
                errors[0].parentNode.scrollIntoView({behavior: 'smooth'});
                clearInterval(firstErrorInterval);
            }
        }, 100);
    };

    const jumpToSelectedStepByStepID = (newStepID) => {
        MLEndorsementFlowUtil.addAndNavigateToSelectedStep(newStepID, steps, currentStepIndex, changeNextSteps, goNext, productCode) 
    };

    const onNext = () => {
        busActivity.toSync.value = false;
        const isValid = busActivity.aspects.valid && busActivity.aspects.subtreeValid;
        if (!isValid) {
            setShowValidationErrors(true);
            setErrorTimestamp(errorTimestamp+1);
        }
        if (busActivity.value.businessActivityQuestionSet && checkForQuestionSetErrors(busActivity.value.businessActivityQuestionSet)) {
            scrollToFirstError();
            return;
        }
        if (!isValid) return;

        if (pageAction === 'add') {
            addNewBusinessActivityAlert(busActivity);
        }

        const lbl = MLEndorsementUtil.stepLabel(currentStep);
        MLEndorsementUtil.setEndorsementDescription(lbl, submissionVM, translator);
        const promise = EndorsementService.saveEndorsement([submissionVM.value], authHeader);
        showProgressDialog(messages.saving, promise);
        promise.then((updatedPolicyChange) => {
            const policyChange = new PolicyChange(Object.assign({}, updatedPolicyChange));
            const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');
            updateWizardSubmissionData(updatedEndorsementVM);       
            const item = busActivity.value;
            const details = {
                data : `${item.name}`,
                additionalData : {fixedID: busActivity.value.fixedID}
            }
            
            MLNavigationStepsTrackingService.completeCurrentStep(details, authHeader ,currentStep); 
            MLTagManagerService.sendFormStepPolicyChangeStep(`${pageAction} Business Activity`);
            jumpToSelectedStepByStepID('endorsementAdditionalChanges');      
        })

    };

    const resolvers = {
        resolveComponentMap: {
            businessDetailsPopupComponent: BusinessDetailsPopupComponent
        },
        resolveCallbackMap: {
            onNext
        },
        resolveClassNameMap: styles
    };

    const overrides = {
        contentContainer: {
            className: isGPA ? 'page-content-gpa' : ''
        },
        addEditHeaderTitle: {
            visible: pageAction === 'add' || pageAction === 'edit',
            content:  pageAction === 'add' ? translator(messages.addBusinessActivity) : translator(messages.editBusinessActivity)
        },
        addEditHeaderMsg: {
            visible: !isGPA
        },
        businessActivityDetailComponent: {
            pupModel,
            onValidate,
            showValidationErrors,
            businessActivityVM: busActivity,
            getUnderlyingPolicies,
            syncExposure: syncBusActivityExposure,
            createUnderlyingPolicy: createNewULPolicy,
            isEndorsement: true,
            showProgressDialog,
            submissionVM,
            onModelChange: () => {
                updateWizardSubmissionData(submissionVM);
            }
        },
        nextDescription: {
            content: getNextStepText()
        },
        stickyContinueSection: {
            visible: isGPA,
            defaultContinueSectionId: 'footer',
            nextDescription: getNextStepText(),
            onNext
        }
    };

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            showNext={false}
            policyChangePageHeaderML={isGPA ? messages.businessActivity : null}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                model={submissionVM}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
                showErrors={showValidationErrors}
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    );
};

BusinessActivityPagePolChange.propTypes = wizardProps;
export default BusinessActivityPagePolChange;